import { Fragment, useState, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { IoChevronDown, IoCheckmarkSharp } from 'react-icons/io5';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@i18n/client';
import useQueryParam from '@utils/use-query-params';
type Option = {
  name: string;
  value: string;
};

export default function ListBox({
  options,
  lang,
}: {
  options: Option[];
  lang: string;
}) {
  const { t } = useTranslation(lang, 'common');
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const { updateQueryParams } = useQueryParam(pathname ?? '/');
  const hasQueryKey = searchParams?.get('sort_by');
  const currentSelectedItem = hasQueryKey
    ? options.find((o) => o.value === hasQueryKey)!
    : options[0];
  const [selectedItem, setSelectedItem] = useState<Option>(currentSelectedItem);

  useEffect(() => {
    setSelectedItem(currentSelectedItem);
  }, [currentSelectedItem]);

  useEffect(() => {
    updateQueryParams('sort_by', currentSelectedItem.value.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedItem.value]);

  function handleItemClick(value: string) {
    updateQueryParams('sort_by', value);
  }

  return (
    <Listbox
      value={selectedItem}
      onChange={({ value }) => handleItemClick(value)}
    >
      {({ open }) => (
        <div className="relative">
          <Listbox.Button className="flex items-center px-3 py-2 text-sm border border-border-base rounded-md hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand focus:ring-opacity-50 transition-colors min-w-[160px]">
            <span className="text-brand-dark font-medium mr-2">
              {t('text-sort-by')}
            </span>
            <span className="text-gray-500 mr-2">{t(selectedItem.name)}</span>
            <IoChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </Listbox.Button>
          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              static
              className="absolute z-20 w-full py-1 mt-1 overflow-auto text-sm rounded-md shadow-lg bg-white border border-border-base max-h-60 focus:outline-none"
            >
              {options?.map((option, idx) => (
                <Listbox.Option
                  key={idx}
                  className={({ active }) =>
                    `cursor-pointer transition-all select-none relative py-2.5 px-4 ${
                      active ? 'text-brand-dark bg-gray-100' : 'bg-white'
                    }`
                  }
                  value={option}
                >
                  {({ selected, active }) => (
                    <div className="flex items-center">
                      <span
                        className={`$${selected ? 'font-medium' : 'font-normal'} block truncate`}
                      >
                        {t(option.name)}
                      </span>
                      {selected ? (
                        <IoCheckmarkSharp
                          className="w-5 h-5 text-brand ml-auto"
                          aria-hidden="true"
                        />
                      ) : null}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
